import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Package, MapPin, Clock, Plane, DollarSign, Shield, User, CreditCard, Building, Smartphone, Banknote, FileText } from 'lucide-react';
import { flag } from '../utils/countryFlags';

const PAYMENT_METHOD_LABELS = {
  card: 'Carte bancaire',
  interac: 'Virement Interac',
  orange_money: 'Orange Money',
  cash: 'Espèces'
};

const PAYMENT_METHOD_ICONS = {
  card: CreditCard,
  interac: Building,
  orange_money: Smartphone,
  cash: Banknote
};

const STATUS_LABELS = {
  pending_admin_validation: 'En attente de validation',
  pending_payment: 'En attente de paiement',
  paid: 'Paiement confirmé',
  awaiting_pickup_seller: 'En attente récupération vendeur',
  received_admin_departure: 'Reçu Admin - Départ',
  in_transit: 'En transit',
  received_admin_destination: 'Reçu Admin - Destination',
  delivered: 'Livré',
  completed: 'Complété',
  rejected: 'Refusée',
  cancelled: 'Annulé',
  refunded: 'Remboursé'
};

const STATUS_COLORS = {
  pending_admin_validation: 'bg-amber-500/20 text-amber-600',
  pending_payment: 'bg-yellow-500/20 text-yellow-500',
  paid: 'bg-amber-500/20 text-amber-500',
  awaiting_pickup_seller: 'bg-orange-500/20 text-orange-500',
  received_admin_departure: 'bg-teal-500/20 text-teal-500',
  in_transit: 'bg-violet-500/20 text-violet-500',
  received_admin_destination: 'bg-sky-500/20 text-sky-500',
  delivered: 'bg-green-500/20 text-green-500',
  completed: 'bg-emerald-500/20 text-emerald-500',
  rejected: 'bg-red-500/20 text-red-500',
  cancelled: 'bg-red-500/20 text-red-500',
  refunded: 'bg-amber-500/20 text-amber-500'
};

const OrderDetailModal = ({ order, open, onClose, formatPrice, isAdmin = false }) => {
  if (!order) return null;

  const PayIcon = PAYMENT_METHOD_ICONS[order.payment_method] || DollarSign;
  const priceFormatter = formatPrice || ((v) => `${(v || 0).toFixed(2)} CAD`);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            Détails de la commande
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Status & ID */}
          <div className="flex items-center justify-between">
            <Badge className={STATUS_COLORS[order.current_status] || 'bg-gray-500/20 text-gray-500'}>
              {STATUS_LABELS[order.current_status] || order.current_status}
            </Badge>
            <span className="text-xs font-mono text-muted-foreground">
              #{order.id?.slice(0, 8).toUpperCase()}
            </span>
          </div>

          {/* Rejection reason */}
          {order.current_status === 'rejected' && order.rejection_reason && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm font-medium text-red-600">Raison du refus :</p>
              <p className="text-sm text-red-500">{order.rejection_reason}</p>
            </div>
          )}

          {/* Payment deadline */}
          {order.current_status === 'pending_payment' && order.payment_deadline && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-600">Date limite de paiement :</p>
              <p className="text-sm text-amber-500">{new Date(order.payment_deadline).toLocaleString('fr-FR')}</p>
            </div>
          )}

          {/* Route */}
          <div className="bg-muted/50 rounded-lg p-4" data-testid="order-detail-route">
            <div className="flex items-center gap-2 mb-2">
              <Plane className="w-4 h-4 text-primary" />
              <span className="font-semibold text-base">
                {flag(order.departure_country)} {order.departure_city} → {flag(order.destination_country)} {order.destination_city}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
              <span>Départ : {order.departure_country || '—'}</span>
              <span>Arrivée : {order.destination_country || '—'}</span>
              {order.airline && <span>Compagnie : {order.airline}</span>}
              {order.travel_date && <span>Vol : {new Date(order.travel_date + 'T12:00:00').toLocaleDateString('fr-FR')}</span>}
            </div>
          </div>

          {/* Participants */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <User className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold uppercase text-primary">Acheteur</span>
              </div>
              {isAdmin ? (
                <>
                  <p className="text-sm font-medium">{order.buyer_name || order.buyer_info?.full_name || '—'}</p>
                  {(order.buyer_email || order.buyer_info?.email) && <p className="text-xs text-muted-foreground">{order.buyer_email || order.buyer_info?.email}</p>}
                </>
              ) : (
                <p className="text-sm font-mono text-muted-foreground">{order.buyer_matricule || order.buyer_info?.matricule || ('KK-' + (order.buyer_id || '').substring(0, 8).toUpperCase()) || '—'}</p>
              )}
            </div>
            <div className="bg-green-500/5 border border-green-500/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <User className="w-3.5 h-3.5 text-green-500" />
                <span className="text-xs font-semibold uppercase text-green-500">Vendeur</span>
              </div>
              {isAdmin ? (
                <p className="text-sm font-medium">{order.seller_name || order.seller_info?.full_name || '—'}</p>
              ) : (
                <p className="text-sm font-mono text-muted-foreground">{order.seller_info?.matricule || ('KK-' + (order.seller_id || '').substring(0, 8).toUpperCase()) || '—'}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Items */}
          <div data-testid="order-detail-items">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <Package className="w-4 h-4" /> Articles commandés
            </h4>
            {order.selected_items && order.selected_items.length > 0 ? (
              <div className="space-y-2">
                {order.selected_items.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-muted/50 rounded-md px-3 py-2 text-sm">
                    <div>
                      <span className="font-medium capitalize">{item.type}</span>
                      <span className="text-muted-foreground ml-2">x {item.quantity}</span>
                    </div>
                    <span className="font-medium">{priceFormatter(item.price_per_unit * item.quantity)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-muted/50 rounded-md px-3 py-2 text-sm">
                <span className="font-medium">{order.quantity || 0} unité(s)</span>
                <span className="text-muted-foreground ml-2">
                  à {priceFormatter(order.price_per_unit || 0)} / unité
                </span>
              </div>
            )}
          </div>

          {/* Article description */}
          {order.article_description && (
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase text-muted-foreground">Description du colis</span>
              </div>
              <p className="text-sm">{order.article_description}</p>
              {order.declared_weight && (
                <p className="text-xs text-muted-foreground mt-1">Poids déclaré : {order.declared_weight} kg</p>
              )}
            </div>
          )}

          {/* Delivery instructions */}
          {order.delivery_instructions && (
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold uppercase text-muted-foreground">Consignes de livraison</span>
              </div>
              <p className="text-sm">{order.delivery_instructions}</p>
            </div>
          )}

          <Separator />

          {/* Price Breakdown */}
          <div data-testid="order-detail-pricing">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
              <DollarSign className="w-4 h-4" /> Détail du prix
            </h4>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sous-total</span>
                <span>{priceFormatter(order.subtotal)}</span>
              </div>
              {(order.commission > 0 || order.buyer_commission > 0) && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Commission ({order.commission_rate || 10}%)
                  </span>
                  <span>{priceFormatter(order.commission || order.buyer_commission)}</span>
                </div>
              )}
              {order.insurance_cost > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Assurance
                  </span>
                  <span>{priceFormatter(order.insurance_cost)}</span>
                </div>
              )}
              <Separator className="my-1" />
              <div className="flex justify-between font-bold text-base">
                <span>Total</span>
                <span className="text-primary">{priceFormatter(order.total)}</span>
              </div>
              {order.seller_amount > 0 && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Montant vendeur</span>
                  <span>{priceFormatter(order.seller_amount)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Insurance info */}
          <div className={`rounded-lg p-3 text-sm ${order.insurance_opt_in ? 'bg-primary/5 border border-primary/20' : 'bg-muted/50'}`} data-testid="order-detail-insurance">
            <div className="flex items-center gap-1.5 mb-1">
              <Shield className={`w-3.5 h-3.5 ${order.insurance_opt_in ? 'text-primary' : 'text-muted-foreground'}`} />
              <span className={`font-semibold ${order.insurance_opt_in ? 'text-primary' : ''}`}>Assurance</span>
              {order.insurance_opt_in ? (
                <Badge className="bg-primary/20 text-primary text-[10px] ml-auto">Souscrite</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] ml-auto">Non souscrite</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              <span>Valeur déclarée :</span>
              <span className="font-medium text-foreground">{order.declared_value > 0 ? priceFormatter(order.declared_value) : '—'}</span>
              {order.insurance_opt_in ? (
                <>
                  <span>Couverture max :</span>
                  <span className="font-medium text-foreground">{order.max_coverage ? priceFormatter(order.max_coverage) : '—'}</span>
                  <span>Coût assurance :</span>
                  <span className="font-medium text-foreground">{order.insurance_cost > 0 ? priceFormatter(order.insurance_cost) : '—'}</span>
                </>
              ) : (
                <>
                  <span>Couverture de base :</span>
                  <span className="font-medium text-foreground">{priceFormatter(order.base_coverage_at_checkout || 100)}</span>
                </>
              )}
            </div>
          </div>

          {/* Payment info */}
          <div className="bg-muted/50 rounded-lg p-3 text-sm" data-testid="order-detail-payment">
            <div className="flex items-center gap-1.5 mb-1">
              <PayIcon className="w-3.5 h-3.5" />
              <span className="font-semibold">Paiement</span>
            </div>
            <div className="grid grid-cols-2 gap-1 text-muted-foreground">
              <span>Méthode :</span>
              <span className="font-medium text-foreground">
                {PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method || 'Carte'}
              </span>
              <span>Statut :</span>
              <span className="font-medium text-foreground">{order.payment_status || '—'}</span>
              {order.payment_deadline && (
                <>
                  <span>Date limite :</span>
                  <span className="font-medium text-foreground">
                    {new Date(order.payment_deadline).toLocaleString('fr-CA')}
                  </span>
                </>
              )}
              <span>Devise :</span>
              <span className="font-medium text-foreground">{order.currency || 'CAD'}</span>
            </div>
          </div>

          {/* Pickup/Delivery */}
          {(order.pickup_cities?.length > 0 || order.delivery_cities?.length > 0) && (
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <MapPin className="w-3.5 h-3.5" />
                <span className="font-semibold">Logistique</span>
              </div>
              {order.pickup_cities?.length > 0 && (
                <p className="text-muted-foreground">Ramassage : <span className="text-foreground">{order.pickup_cities.join(' ou ')}</span></p>
              )}
              {order.delivery_cities?.length > 0 && (
                <p className="text-muted-foreground">Livraison : <span className="text-foreground">{order.delivery_cities.join(' ou ')}</span></p>
              )}
              {order.delivery_delay && (
                <p className="text-muted-foreground">Délai : <span className="text-foreground">{order.delivery_delay}</span></p>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Créée le {new Date(order.created_at).toLocaleString('fr-CA')}
            </div>
            {order.delivered_at && (
              <span>Livrée le {new Date(order.delivered_at).toLocaleString('fr-CA')}</span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailModal;
